<template>
  <div v-if="visible" class="image-preview-overlay" @click="close">
    <div class="image-preview-container" @click.stop>
      <!-- 关闭按钮 -->
      <div class="preview-close" @click="close">
        <span class="close-icon">✕</span>
      </div>

      <!-- 左侧切换 -->
      <div v-if="images.length > 1" class="nav-btn nav-left" @click.stop="prev">‹</div>
      
      <!-- 右侧切换 -->
      <div v-if="images.length > 1" class="nav-btn nav-right" @click.stop="next">›</div>

      <!-- 图片计数 -->
      <div class="preview-counter">{{ currentIndex + 1 }} / {{ images.length }}</div>

      <!-- 图片展示区域 -->
      <div
        class="preview-swipe-area"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
      >
        <div
          class="preview-images-track"
          :style="{ transform: `translateX(${translateX}px)`, transition: isTransitioning ? 'transform 0.3s ease' : 'none' }"
        >
          <div
            v-for="(img, index) in images"
            :key="index"
            class="preview-image-wrapper"
          >
            <img
              :src="img"
              class="preview-image"
            />
          </div>
        </div>
      </div>

      <!-- 指示点 -->
      <div v-if="images.length > 1" class="preview-dots">
        <span
          v-for="(img, index) in images"
          :key="index"
          :class="['preview-dot', { active: index === currentIndex }]"
          @click="goTo(index)"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImagePreview',
  data() {
    return {
      visible: false,
      images: [],
      currentIndex: 0,
      translateX: 0,
      startX: 0,
      isDragging: false,
      isTransitioning: true,
      containerWidth: 0
    };
  },
  methods: {
    /**
     * 打开图片预览
     * @param {Array<string>} imageList - 图片 URL 列表
     * @param {number} startIndex - 初始显示的图片索引
     */
    open(imageList, startIndex = 0) {
      if (!imageList || imageList.length === 0) return;
      this.images = imageList;
      this.currentIndex = Math.min(startIndex, imageList.length - 1);
      this.visible = true;
      this.translateX = -this.currentIndex * window.innerWidth;
      // 禁止背景滚动
      document.body.style.overflow = 'hidden';
      // 测量容器宽度
      this.$nextTick(() => {
        const area = this.$el?.querySelector('.preview-swipe-area');
        if (area) {
          this.containerWidth = area.offsetWidth;
        } else {
          this.containerWidth = window.innerWidth;
        }
        this.translateX = -this.currentIndex * this.containerWidth;
      });
    },
    close() {
      this.visible = false;
      this.currentIndex = 0;
      this.images = [];
      this.isDragging = false;
      this.isTransitioning = true;
      document.body.style.overflow = '';
      this.$emit('close');
    },
    goTo(index) {
      this.currentIndex = index;
      this.translateX = -index * this.containerWidth;
    },
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.translateX = -this.currentIndex * this.containerWidth;
      }
    },
    next() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
        this.translateX = -this.currentIndex * this.containerWidth;
      }
    },
    // ===== 触摸事件 =====
    onTouchStart(e) {
      this.isDragging = true;
      this.startX = e.touches[0].clientX;
      this.isTransitioning = false;
    },
    onTouchMove(e) {
      if (!this.isDragging) return;
      const deltaX = e.touches[0].clientX - this.startX;
      this.translateX = -this.currentIndex * this.containerWidth + deltaX;
    },
    onTouchEnd(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.isTransitioning = true;
      const deltaX = e.changedTouches[0].clientX - this.startX;
      this.handleSwipe(deltaX);
    },
    // ===== 鼠标事件（桌面端） =====
    onMouseDown(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.isTransitioning = false;
      e.preventDefault();
    },
    onMouseMove(e) {
      if (!this.isDragging) return;
      const deltaX = e.clientX - this.startX;
      this.translateX = -this.currentIndex * this.containerWidth + deltaX;
    },
    onMouseUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.isTransitioning = true;
      const deltaX = e.clientX - this.startX;
      this.handleSwipe(deltaX);
    },
    // ===== 滑动处理 =====
    handleSwipe(deltaX) {
      const threshold = this.containerWidth * 0.25;
      if (Math.abs(deltaX) < threshold) {
        // 滑动距离不够，回弹
        this.translateX = -this.currentIndex * this.containerWidth;
        return;
      }
      if (deltaX < 0) {
        // 左滑，下一张
        this.next();
      } else {
        // 右滑，上一张
        this.prev();
      }
    }
  }
};
</script>

<style scoped>
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.image-preview-container {
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

/* 关闭按钮 */
.preview-close {
  position: absolute;
  top: -40px;
  right: 0;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: background 0.2s;
}

.preview-close .close-icon {
  font-size: 18px;
  color: white;
  line-height: 1;
  font-weight: 300;
}

.preview-close:active {
  background: rgba(255, 255, 255, 0.4);
}

/* 左右切换按钮 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s;
  user-select: none;
}

.nav-btn:active {
  background: rgba(255, 255, 255, 0.35);
}

.nav-left {
  left: 8px;
}

.nav-right {
  right: 8px;
}

/* 图片计数 */
.preview-counter {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  z-index: 10;
  font-weight: 500;
}

/* 滑动区域 */
.preview-swipe-area {
  width: 100%;
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  max-height: 70vh;
}

/* 图片轨道 */
.preview-images-track {
  display: flex;
  height: 100%;
  width: 100%;
  will-change: transform;
}

/* 图片容器 */
.preview-image-wrapper {
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

/* 图片 */
.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

/* 指示点 */
.preview-dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 10;
}

.preview-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.2s;
}

.preview-dot.active {
  background: white;
  transform: scale(1.2);
}
</style>
